import styled, { css, keyframes } from 'styled-components';
import { colors, gradients, spacing } from '../../constants/theme';

export const Badge = styled.span<{ 
  variant?: 'primary' | 'success' | 'info' | 'warning' | 'error';
  size?: 'sm' | 'md' | 'lg';
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  font-weight: 600;
  
  ${props => {
    switch (props.size) {
      case 'sm':
        return css`
          padding: 4px 8px;
          font-size: 11px;
        `;
      case 'lg':
        return css`
          padding: 8px 16px;
          font-size: 14px;
        `;
      default:
        return css`
          padding: 6px 12px;
          font-size: 12px;
        `;
    }
  }}
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return css`
          background: ${colors.success}20;
          color: ${colors.success};
          border: 1px solid ${colors.success}30;
        `;
      case 'info':
        return css`
          background: ${colors.info}20;
          color: ${colors.info};
          border: 1px solid ${colors.info}30;
        `;
      case 'warning':
        return css`
          background: ${colors.warning}20;
          color: ${colors.warning};
          border: 1px solid ${colors.warning}30;
        `;
      case 'error':
        return css`
          background: ${colors.error}20;
          color: ${colors.error};
          border: 1px solid ${colors.error}30;
        `;
      default:
        return css`
          background: ${colors.primary.main}20;
          color: ${colors.primary.main};
          border: 1px solid ${colors.primary.main}30;
        `;
    }
  }}
`;

export const Avatar = styled.div<{ 
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'primary' | 'secondary';
}>`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  ${props => {
    const sizes = {
      sm: { size: '32px', fontSize: '12px' },
      md: { size: '40px', fontSize: '14px' },
      lg: { size: '56px', fontSize: '18px' },
      xl: { size: '80px', fontSize: '24px' }
    };
    const config = sizes[props.size || 'md'];
    
    return css`
      width: ${config.size};
      height: ${config.size};
      font-size: ${config.fontSize};
    `;
  }}

  background: ${props => props.variant === 'secondary' 
    ? gradients.secondary 
    : gradients.primary
  };
  color: ${colors.text.primary};

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 20px ${colors.primary.main}30;
  }
`;

export const Divider = styled.hr<{ 
  orientation?: 'horizontal' | 'vertical';
  spacing?: 'sm' | 'md' | 'lg';
}>`
  border: none;
  background: ${colors.background.tertiary};
  
  ${props => props.orientation === 'vertical' ? css`
    width: 1px;
    height: 100%;
    margin: 0 ${spacing.md};
  ` : css`
    height: 1px;
    width: 100%;
    margin: ${(() => {
      switch (props.spacing) {
        case 'sm': return `${spacing.sm} 0`;
        case 'lg': return `${spacing.lg} 0`;
        default: return `${spacing.md} 0`;
      }
    })()};
  `}
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const LoadingSpinner = styled.div<{ size?: 'sm' | 'md' | 'lg' }>`
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  
  ${props => {
    switch (props.size) {
      case 'sm':
        return css`
          width: 20px;
          height: 20px;
          border: 2px solid ${colors.background.tertiary};
          border-top: 2px solid ${colors.primary.main};
        `;
      case 'lg':
        return css`
          width: 60px;
          height: 60px;
          border: 4px solid ${colors.background.tertiary};
          border-top: 4px solid ${colors.primary.main};
        `;
      default:
        return css`
          width: 40px;
          height: 40px;
          border: 3px solid ${colors.background.tertiary};
          border-top: 3px solid ${colors.primary.main};
        `;
    }
  }}
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing.xxl} ${spacing.lg};
  color: ${colors.text.secondary};
`;

export const EmptyIcon = styled.div`
  width: 100px;
  height: 100px;
  background: ${colors.background.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin: 0 auto ${spacing.lg};
  border: 2px solid ${colors.background.tertiary};
`;

export const EmptyStateTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 ${spacing.md} 0;
`;

export const EmptyStateDescription = styled.p`
  font-size: 16px;
  color: ${colors.text.secondary};
  margin: 0;
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
`;

export const StatusMessage = styled.div<{ variant: 'success' | 'error' | 'warning' | 'info' }>`
  padding: ${spacing.md};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  margin-top: ${spacing.md};
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return css`
          background: linear-gradient(135deg, #10B981, #059669);
          color: white;
        `;
      case 'error':
        return css`
          background: linear-gradient(135deg, #EF4444, #DC2626);
          color: white;
        `;
      case 'warning':
        return css`
          background: ${colors.warning}20;
          color: ${colors.warning};
          border: 1px solid ${colors.warning}30;
        `;
      case 'info':
        return css`
          background: ${colors.info}20;
          color: ${colors.info};
          border: 1px solid ${colors.info}30;
        `;
    }
  }}
`;

export const Highlight = styled.span`
  background: ${colors.primary.main}20;
  color: ${colors.primary.main};
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 600;
  border: 1px solid ${colors.primary.main}30;
`;

export const Tooltip = styled.div<{ visible?: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${colors.background.secondary};
  color: ${colors.text.primary};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  border: 1px solid ${colors.background.tertiary};
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${colors.background.tertiary};
  }
`;
